import {useState, useRef, useEffect} from "react";
import {Button} from "../ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription
} from "../ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import * as Slider from "@radix-ui/react-slider";
import {cn} from "../../lib/utils";
import {Card, CardContent} from "../ui/card";
import {Input} from "../ui/input";
import {Checkbox} from "../ui/checkbox";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {useLocation} from "wouter";
import {useShuffleBooking} from "../../hooks/useShuffleBooking";
import {AnimatePresence, motion} from "framer-motion";

const timeSlots = [
    "17:00 - 18:00",
    "18:00 - 19:00",
    "19:00 - 20:00",
    "20:00 - 21:00",
    "21:00 - 22:00",
];

const priceMarks = [15, 25, 40, 55, 70, 85, 100];

const dietaryOptions = [
    {id: "vegetarian", label: "Vegetarian"},
    {id: "vegan", label: "Vegan"},
    {id: "gluten-free", label: "Gluten-Free"},
    {id: "dairy-free", label: "Dairy-Free"},
    {id: "nut-free", label: "Nut-Free"},
    {id: "kosher", label: "Kosher"},
    {id: "halal", label: "Halal"},
];

const cities = [
    "Amsterdam",
    "Berlin",
    "London",
    "Madrid",
    "Paris",
    "Rome",
    "Vienna",
];

const priceOptions = [
    {value: "15", label: "€15 per person"},
    {value: "25", label: "€25 per person"},
    {value: "45", label: "€45 per person"},
    {value: "65", label: "€65 per person"},
];

const formSchema = z.object({
    location: z.string().min(1, {message: "Please select a location"}),
    time: z.string().min(1, {message: "Please select a time slot"}),
    guests: z.string().min(1, "Please enter number of guests"),
    dietaryRestrictions: z.array(z.string()).optional(),
    pricePerPerson: z.string().min(1, {message: "Please select a price"}),
    date: z
        .string()
        .min(1, {message: "Please select a date"})
        .refine((val) => {
            const selected = new Date(val);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // strip time
            return selected >= today;
        }, {
            message: "Date cannot be in the past",
        }),
});

type FormValues = z.infer<typeof formSchema>;

const modalMessages = [
    {text: "We are processing your surprise... 🍽️", emoji: "🍽️"},
    {text: "Don't worry, we know you’re in a hurry! ⏱️", emoji: "⏱️"},
    {text: "You’ll have a delicious experience! 😋", emoji: "😋"},
    {text: "Our system is finding your best match! 🧑‍🍳", emoji: "🧑‍🍳"},
    {text: "Are you ready for the results? 🎉", emoji: "🎉"}
];

const BookingSurprise = () => {
    const [, setLocation] = useLocation();
    const {loading, error, bookShuffle} = useShuffleBooking();
    const [showModal, setShowModal] = useState(false);
    const [modalStep, setModalStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const modalTimer = useRef<NodeJS.Timeout | null>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            location: "",
            date: "",
            time: "",
            guests: "2",
            dietaryRestrictions: [],
            pricePerPerson: "15", // Default to €15
        },
    });

    // Handle Modal Countdown and Progress
    const startModalSequence = async (data: FormValues) => {
        setShowModal(true);
        setModalStep(0);
        setProgress(0);

        let elapsed = 0;
        let step = 0;
        const interval = 1; // seconds
        const total = 5;   // seconds

        modalTimer.current = setInterval(() => {
            elapsed += interval;
            step = Math.min(Math.floor(elapsed / interval), modalMessages.length - 1);
            setModalStep(step);
            setProgress((elapsed / total) * 100);

            if (elapsed >= total) {
                clearInterval(modalTimer.current!);
                setProgress(100);
                setTimeout(async () => {
                    try {
                        const result = await bookShuffle(data);
                        if (!result) throw new Error(error || "Booking failed");
                        localStorage.setItem("shuffleResult", JSON.stringify(result.restaurants));
                        localStorage.setItem("shuffleBookingContext",
                            JSON.stringify({
                                ...data,
                                session_id: result.session_id,
                            })
                        );
                        setShowModal(false);
                        setLocation("/shuffle");
                    } catch (err) {
                        setShowModal(false);
                        alert(error || "Booking failed. Try again!");
                    }
                }, 900); // last suspense
            }
        }, interval * 1000);
    };

    useEffect(() => {
        return () => {
            if (modalTimer.current) clearInterval(modalTimer.current);
        };
    }, []);

    // --- Submit Handler ---
    const onSubmit = async (data: FormValues) => {
        startModalSequence(data);
    };

    return (
        <section className="bg-gray-50 py-16 transition-colors duration-200 dark:bg-gray-800/50 md:py-20" id="booking-section">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-10 text-center">
                    <h2 className="mb-4 font-serif text-3xl font-bold text-gray-800 dark:text-white md:text-4xl">
                        Book Your Surprise Meal
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
                        Let us surprise you with a premium dining experience at an unexpected restaurant
                        for just €15 per person.
                    </p>
                </div>
                <Card className="mx-auto max-w-3xl bg-white shadow-lg dark:bg-gray-900">
                    <CardContent className="px-6 py-8">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                <div className="grid gap-6 md:grid-cols-2">
                                    {/* Location */}
                                    <div>
                                        <FormField
                                            control={form.control}
                                            name="location"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel
                                                        className="text-gray-700 dark:text-gray-200">Location</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger
                                                                className="border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                                                                <SelectValue placeholder="Select a city"/>
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {cities.map((city) => (
                                                                <SelectItem key={city} value={city}>
                                                                    {city}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    {/* Date */}
                                    <div>
                                        <FormField
                                            control={form.control}
                                            name="date"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel
                                                        className="text-gray-700 dark:text-gray-200">Date</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="date"
                                                            className="border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    {/* Time */}
                                    <div>
                                        <FormField
                                            control={form.control}
                                            name="time"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel
                                                        className="text-gray-700 dark:text-gray-200">Time</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger
                                                                className="border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                                                                <SelectValue placeholder="Select a time slot"/>
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {timeSlots.map((slot) => (
                                                                <SelectItem key={slot} value={slot}>
                                                                    {slot}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    {/* Guests */}
                                    <div>
                                        <FormField
                                            control={form.control}
                                            name="guests"
                                            render={({field}) => (
                                                <FormItem>
                                                    <FormLabel className="text-gray-700 dark:text-gray-200">
                                                        Number of Guests
                                                    </FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger
                                                                className="border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                                                                <SelectValue placeholder="Select number of guests"/>
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                                                <SelectItem key={num} value={num.toString()}>
                                                                    {num} {num === 1 ? "guest" : "guests"}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                                {/* Dietary Restrictions */}
                                <div>
                                    <FormField
                                        control={form.control}
                                        name="dietaryRestrictions"
                                        render={() => (
                                            <FormItem>
                                                <div className="mb-4">
                                                    <FormLabel className="text-gray-700 dark:text-gray-200">
                                                        Dietary Restrictions
                                                    </FormLabel>
                                                    <FormDescription
                                                        className="text-sm text-gray-500 dark:text-gray-400">
                                                        Select all that apply
                                                    </FormDescription>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                                                    {dietaryOptions.map((option) => (
                                                        <FormField
                                                            key={option.id}
                                                            control={form.control}
                                                            name="dietaryRestrictions"
                                                            render={({field}) => (
                                                                <FormItem
                                                                    key={option.id}
                                                                    className="flex flex-row items-start space-x-3 space-y-0"
                                                                >
                                                                    <FormControl>
                                                                        <Checkbox
                                                                            checked={field.value?.includes(option.id)}
                                                                            onCheckedChange={(checked) => {
                                                                                return checked
                                                                                    ? field.onChange([...(field.value || []), option.id])
                                                                                    : field.onChange(field.value?.filter((value) => value !== option.id));
                                                                            }}
                                                                        />
                                                                    </FormControl>
                                                                    <FormLabel
                                                                        className="font-normal">{option.label}</FormLabel>
                                                                </FormItem>
                                                            )}
                                                        />
                                                    ))}
                                                </div>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />
                                    {/* Price per Person */}
                                    <FormField
                                        control={form.control}
                                        name="pricePerPerson"
                                        render={({field}) => (
                                            <FormItem className="mt-6">
                                                <FormLabel className="text-gray-700 dark:text-gray-200">
                                                    Max Price per Person You Are Willing to Pay
                                                </FormLabel>
                                                <div className="px-2 py-3 relative">
                                                    <Slider.Root
                                                        className="relative flex items-center select-none touch-none w-full h-10"
                                                        min={priceMarks[0]}
                                                        max={priceMarks[priceMarks.length - 1]}
                                                        step={1}
                                                        value={[Number(field.value || priceMarks[0])]}
                                                        onValueChange={([val]) => {
                                                            let closest = priceMarks.reduce((prev, curr) =>
                                                                Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
                                                            );
                                                            field.onChange(closest.toString());
                                                        }}
                                                    >
                                                        {/* TRACK */}
                                                        <Slider.Track
                                                            className="bg-gray-200 dark:bg-gray-700 relative grow rounded-full h-2">
                                                            <Slider.Range
                                                                className="absolute bg-primary h-2 rounded-full"/>
                                                        </Slider.Track>
                                                        {/* THUMB */}
                                                        {(() => {
                                                            const currentIndex = priceMarks.findIndex(
                                                                (m) => m === Number(field.value)
                                                            );
                                                            const percent =
                                                                currentIndex >= 0
                                                                    ? (currentIndex / (priceMarks.length - 1)) * 100
                                                                    : 0;
                                                            return (
                                                                <Slider.Thumb
                                                                    className="block w-6 h-6 bg-primary rounded-full shadow focus:outline-none border-2 border-primary"
                                                                    aria-label="Price"
                                                                    style={{
                                                                        left: `calc(${percent}%)`,
                                                                        position: "absolute",
                                                                        top: "50%",
                                                                        transform: "translate(-50%, -50%)", // fixes both axes
                                                                    }}
                                                                />
                                                            );
                                                        })()}
                                                    </Slider.Root>
                                                    {/* MARKS */}
                                                    <div className="flex justify-between text-xs mt-2 px-1 relative">
                                                        {priceMarks.map((mark, idx) => (
                                                            <span
                                                                key={mark}
                                                                className={cn(
                                                                    "w-8 text-center absolute",
                                                                    field.value == mark.toString()
                                                                        ? "text-primary font-bold"
                                                                        : "text-gray-400 dark:text-gray-500"
                                                                )}
                                                                style={{
                                                                    left: `calc(${(idx / (priceMarks.length - 1)) * 100}%)`,
                                                                    transform: "translateX(-50%)",
                                                                }}
                                                            >
                                                                €{mark}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />


                                </div>
                                <Button
                                    type="submit"
                                    className="mt-6 w-full bg-primary py-6 text-lg font-medium text-white hover:bg-primary/90"
                                    disabled={showModal}
                                >
                                    Find My Surprise
                                </Button>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </div>
            {/* Animated Modal */}
            <AnimatePresence>
                {showModal ? (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
                    >
                        <motion.div
                            initial={{scale: 0.92, opacity: 0, y: 36}}
                            animate={{scale: 1, opacity: 1, y: 0}}
                            exit={{scale: 0.95, opacity: 0, y: 36}}
                            transition={{type: "spring", stiffness: 300, damping: 24}}
                            className="relative w-full max-w-md rounded-2xl bg-white p-10 shadow-xl dark:bg-gray-800 flex flex-col items-center"
                        >
                            <span className="text-5xl mb-6 animate-bounce">{modalMessages[modalStep].emoji}</span>
                            <motion.h3
                                key={modalStep}
                                initial={{opacity: 0, y: 20}}
                                animate={{opacity: 1, y: 0}}
                                exit={{opacity: 0, y: -20}}
                                transition={{duration: 0.3}}
                                className="text-xl font-bold text-gray-800 dark:text-white text-center"
                            >
                                {modalMessages[modalStep].text}
                            </motion.h3>

                            <div className="w-full mt-8">
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                                    <motion.div
                                        className="bg-primary h-4 rounded-full"
                                        initial={{width: 0}}
                                        animate={{width: `${progress}%`}}
                                        transition={{duration: 0.5, ease: "easeInOut"}}
                                    />
                                </div>
                                <div className="mt-3 text-center text-sm text-gray-500 dark:text-gray-300">
                  <span>
                    {progress < 100
                        ? `${Math.round(progress)}%`
                        : "Almost there..."}
                  </span>
                                </div>
                            </div>
                            <motion.div
                                initial={{opacity: 0}}
                                animate={{opacity: 0.9}}
                                transition={{delay: 0.8}}
                                className="absolute top-4 right-6 text-lg text-gray-400"
                            >
                                <span className="animate-spin inline-block">🍜</span>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </section>
    );
};

export default BookingSurprise;
